import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Action } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { isFormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";
import { DefaultWidgetMap } from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { Button } from "@com.mgmtp.a12.widgets/widgets-core/lib/button";
import { ButtonGroup } from "@com.mgmtp.a12.widgets/widgets-core/lib/button-group";
import { ActionContentboxProps, ContentBoxElements } from "@com.mgmtp.a12.widgets/widgets-core/lib/contentbox";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon";
import { ValidationBar } from "@com.mgmtp.a12.widgets/widgets-core/lib/validation-bar";

import { MobileNavigationContext } from "../../../app/mobileNavigationContext";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { PermissionDataholder } from "../../../types/permission";
import { assertNonNullable } from "../../../utilities/assert";
import { useHasTaskPermission } from "../../../utilities/hooks/permissions";
import { hasUpdateDocumentPermission } from "../../../utilities/permissionUtils";
import { selectHasNeededPermissions } from "../../../utilities/user";
import { getViewHtmlId } from "../../focusHandling/sagas";
import { ANTRAG_DETAIL_ACTIVITY_ID, TASK_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { PERMISSON_DATAHOLDER } from "../../processOverview/store/constants";
import { selectOperationLoadingState } from "../../../app/slices/operation/selectors";
import { DOC_LOCK_OPERATION } from "../../../app/slices/operation/types";

import { editAntragAction } from "../sagas/actions";

import { ActivityContext } from "./contexts";
import { ProcessWizard } from "./wizard";

export function ContentBoxWithWizard(props: ActionContentboxProps): React.ReactNode {
	const activityId = React.useContext(ActivityIdContext);
	assertNonNullable(activityId);
	const activity = useSelector(ActivitySelectors.activityById(activityId));
	const taskActivity = useSelector(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
	const hasNavigation = React.useContext(ActivityContext)?.hasNavigation;
	const archivalInProgress = React.useContext(ActivityContext)?.archivalInProgress;
	const isProcessLocked = React.useContext(ActivityContext)?.isProcessLocked;
	const lockProcessErrorMessage = React.useContext(ActivityContext)?.lockProcessErrorMessage;
	const isReadonly = React.useContext(ActivityContext)?.isReadonly;
	const navigationContext = React.useContext(MobileNavigationContext);
	const vorgangsnummer = React.useContext(ActivityContext)?.vorgangsnummer;
	const fvShortName = React.useContext(ActivityContext)?.fvManifest?.identification?.shortName;
	const fvKey = React.useContext(ActivityContext)?.fvManifest?.identification.key;
	const dispatch = useDispatch();

	const isAntragsActivityDirty = useSelector(ActivitySelectors.dirty(ANTRAG_DETAIL_ACTIVITY_ID));
	const hasCancelPermission = useSelector(selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey));

	const cancelArchivalProcess = React.useCallback(
		() =>
			dispatch(
				ActivityActions.create({
					activityDescriptor: {
						modal: "EldoradoCancelModal",
						jobUuid: archivalInProgress,
						instance: activity?.descriptor.instance
					},
					loadingState: "without"
				})
			),
		[activity?.descriptor.instance, archivalInProgress, dispatch]
	);

	const contentRef = React.useRef<HTMLDivElement | null>(null);
	const getContentRef = React.useCallback((instance: HTMLDivElement | null) => {
		contentRef.current = instance;
	}, []);
	const hasTaskPermission = useHasTaskPermission();
	if (contentRef.current !== null && hasTaskPermission !== undefined) {
		const permission = hasTaskPermission ? "HAS_PERMISSION" : "NO_TASK_PERMISSION";
		contentRef.current.setAttribute("data-test", permission);
	}

	const docLockOperationLoadingState = useSelector(selectOperationLoadingState(DOC_LOCK_OPERATION));

	if (docLockOperationLoadingState) {
		contentRef.current?.setAttribute("data-test-doclock", docLockOperationLoadingState);
	}

	if (activity === undefined) {
		return null;
	}

	const descriptor = activity.descriptor;
	const canEdit = isEditable(descriptor);
	const isTaskEditingPermitted = !archivalInProgress && isEditingPermitted(taskActivity);

	const onBackButtonClicked = props.listenToNavigationContext ? navigationContext?.onBackButtonClicked : undefined;

	const backButton = onBackButtonClicked ? (
		<ContentBoxElements.BackButton onBackButtonClicked={onBackButtonClicked} />
	) : undefined;

	const onForwardButtonClicked = props.listenToNavigationContext
		? navigationContext?.onForwardButtonClicked
		: undefined;

	const forwardButton = onForwardButtonClicked ? (
		<ForwardButton onForwardButtonClicked={onForwardButtonClicked} />
	) : undefined;

	const onCloseButtonClicked = (): void => {
		if (isAntragsActivityDirty) {
			dispatch(ActivityActions.cancelRequested({ activityIds: [ANTRAG_DETAIL_ACTIVITY_ID] }));
		} else {
			dispatch(ActivityActions.cancelRequested({ activityIds: [activity.id] }));
		}
	};

	const notificationAreaProps = getNotificationAreaProps(
		hasNavigation,
		archivalInProgress,
		isProcessLocked,
		hasCancelPermission,
		cancelArchivalProcess,
		lockProcessErrorMessage
	);

	return (
		<DefaultWidgetMap.ActionContentbox
			{...props}
			padding="0px 40px 40px"
			id={getViewHtmlId(activity.id)}
			listenToNavigationContext={false}
			contentRef={getContentRef}
			headingPrefixes={
				<>
					{backButton}
					{forwardButton}
					{props.headingPrefixes}
				</>
			}
			navigation={hasNavigation && <ProcessWizard activityId={activity.id} />}
			headingButtons={
				<>
					{canEdit && isTaskEditingPermitted && !archivalInProgress && !isProcessLocked && (
						<ButtonGroup>
							<Button
								title="Editieren"
								icon={<Icon>edit</Icon>}
								primary
								disabled={!isReadonly}
								onClick={() => dispatch(editAntragAction({ activityId: activity.id }))}
							/>
						</ButtonGroup>
					)}

					{hasNavigation && (
						<ButtonGroup>
							<Button
								title="Schließen"
								icon={<Icon>close</Icon>}
								secondary
								onClick={onCloseButtonClicked}
							/>
						</ButtonGroup>
					)}
				</>
			}
			headingElements={
				<CustomFormHeader
					defaultHeadingElements={props.headingElements}
					fvShortName={fvShortName}
					vorgangsnummer={vorgangsnummer}
					hasNavigation={hasNavigation}
					activityId={activity.id}
				/>
			}
			{...notificationAreaProps}
		>
			{props.children}
		</DefaultWidgetMap.ActionContentbox>
	);
}

function CustomFormHeader(props: {
	defaultHeadingElements: React.ReactNode;
	fvShortName?: string;
	vorgangsnummer?: string;
	hasNavigation?: boolean;
	activityId: string;
}): React.ReactElement {
	const { hasNavigation, defaultHeadingElements, fvShortName, vorgangsnummer, activityId } = props;
	const formModel = useSelector(ModelSelectors.modelInScene({ activityId, modelType: "form" }, isFormModel));
	const title = formModel?.header.labels?.find(e => e.locale === "de")?.text ?? "Antrag";

	return (
		<>
			{!hasNavigation ? (
				<ContentBoxElements.Title text={`${title} (${fvShortName})`} ariaLevel={1} />
			) : (
				defaultHeadingElements
			)}
			{vorgangsnummer ? <ContentBoxElements.Subtitle text={vorgangsnummer} /> : undefined}
		</>
	);
}

function ForwardButton(props: { onForwardButtonClicked?(): void }): React.ReactElement {
	return (
		<ContentBoxElements.HeadingAddon>
			<Button title="Vorwärts" invert onClick={props.onForwardButtonClicked} icon={<Icon>chevron_right</Icon>} />
		</ContentBoxElements.HeadingAddon>
	);
}

function isEditingPermitted(taskActivity: Activity | undefined): boolean {
	const permissionDataholder = taskActivity?.dataHolders?.find(
		dh => dh.descriptor?.[PERMISSON_DATAHOLDER] === PERMISSON_DATAHOLDER
	) as Activity.DataHolder<PermissionDataholder> | undefined;
	return permissionDataholder?.data?.isUserPermittedForTask ?? false;
}

function isEditable(el: Activity.Descriptor): el is { editable: string } {
	return typeof el.editable === "string";
}

function getNotificationAreaProps(
	hasNavigation: undefined | boolean,
	archivalInProgress: string | undefined,
	isProcessLocked: undefined | boolean,
	hasCancelPermission: boolean,
	cancelArchivalProcess: () => Action<ActivityActions.PushPayload>,
	lockProcessErrorMessage: string | undefined
): { notificationArea?: React.ReactNode } {
	if (hasNavigation && archivalInProgress) {
		return {
			notificationArea: (
				<div>
					<ValidationBar
						variant="info"
						primaryTitle="Prozess wird veraktet"
						secondaryTitle="Dieser Prozess wird momentan an ELDORADO veraktet und kann nicht bearbeitet werden."
						quickAccessMenu={
							hasCancelPermission && !isProcessLocked ? (
								<Button style={{ backgroundColor: "white" }} onClick={cancelArchivalProcess}>
									Veraktung abbrechen
								</Button>
							) : (
								<></>
							)
						}
					/>
				</div>
			)
		};
	}
	if (!hasNavigation && isProcessLocked) {
		return {
			notificationArea: (
				<div>
					<ValidationBar
						variant="warning"
						primaryTitle="Der Vorgang ist gesperrt."
						secondaryTitle={lockProcessErrorMessage}
					/>
				</div>
			)
		};
	}
	return {};
}
